import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ArrowLeft, Users, CalendarCheck, TrendingUp, TrendingDown, Loader2, BarChart3, XCircle, Calendar as CalendarIcon, ArrowUpRight, ArrowDownRight, CloudSun, Sun, Cloud, CloudRain, Snowflake, Thermometer, Info, Baby } from "lucide-react";
import { Link } from "react-router-dom";
import { PiktableLoader } from "@/components/PiktableLoader";
import { useState, useMemo } from "react";
import { useStatisticsData } from "@/hooks/useStatisticsData";
import { useWeatherStatistics } from "@/hooks/useWeatherStatistics";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, LineChart, Line, ComposedChart, Area
} from "recharts";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subDays, startOfYear, endOfYear, subMonths } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";

type PeriodPreset = 'today' | '7days' | '30days' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'custom';

// Weather icons mapping
const WeatherIcon = ({ category, className }: { category: string; className?: string }) => {
  switch (category) {
    case 'sunny':
      return <Sun className={cn("text-amber-500", className)} />;
    case 'cloudy':
      return <Cloud className={cn("text-slate-400", className)} />;
    case 'rainy':
      return <CloudRain className={cn("text-blue-500", className)} />;
    case 'snowy':
      return <Snowflake className={cn("text-cyan-400", className)} />;
    default:
      return <CloudSun className={cn("text-muted-foreground", className)} />;
  }
};

// Weather category colors for charts
const WEATHER_COLORS: Record<string, string> = {
  sunny: 'hsl(45, 93%, 47%)',
  cloudy: 'hsl(215, 14%, 64%)',
  rainy: 'hsl(217, 91%, 60%)',
  snowy: 'hsl(187, 92%, 69%)',
};

const Statistics = () => {
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('thisMonth');
  const [customStartDate, setCustomStartDate] = useState<Date>(startOfMonth(new Date()));
  const [customEndDate, setCustomEndDate] = useState<Date>(endOfMonth(new Date()));

  // Calculate date range based on preset
  const { startDate, endDate } = useMemo(() => {
    const today = new Date();
    switch (periodPreset) {
      case 'today':
        return { startDate: today, endDate: today };
      case '7days':
        return { startDate: subDays(today, 6), endDate: today };
      case '30days':
        return { startDate: subDays(today, 29), endDate: today };
      case 'thisMonth':
        return { startDate: startOfMonth(today), endDate: endOfMonth(today) };
      case 'lastMonth':
        const lastMonth = subMonths(today, 1);
        return { startDate: startOfMonth(lastMonth), endDate: endOfMonth(lastMonth) };
      case 'thisYear':
        return { startDate: startOfYear(today), endDate: endOfYear(today) };
      case 'custom':
        return { startDate: customStartDate, endDate: customEndDate };
      default:
        return { startDate: startOfMonth(today), endDate: endOfMonth(today) };
    }
  }, [periodPreset, customStartDate, customEndDate]);

  const { data, loading, error, refetch } = useStatisticsData({ startDate, endDate });
  const { data: weatherData, loading: weatherLoading } = useWeatherStatistics({ startDate, endDate });

  // Chart data for 12 months evolution
  const chartData = useMemo(() => {
    if (!data?.monthlyData) return [];
    return data.monthlyData.slice(0, 12).reverse().map(m => ({
      name: m.label.split(' ')[0].substring(0, 3),
      venues: m.arrived,
      annulées: m.cancelled,
      personnes: m.arrivedPeople,
      tauxVenue: m.arrivalRate
    }));
  }, [data?.monthlyData]);

  // Pie chart colors
  const COLORS = ['hsl(var(--primary))', 'hsl(var(--destructive))', 'hsl(var(--muted-foreground))'];

  const handlePresetClick = (preset: PeriodPreset) => {
    setPeriodPreset(preset);
  };

  const formatTrend = (value: number, suffix: string = '') => {
    if (value > 0) return `+${value}${suffix}`;
    return `${value}${suffix}`;
  };

  if (loading) {
    return <PiktableLoader message="Chargement des statistiques..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au tableau de bord
          </Link>
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">{error}</p>
              <Button onClick={refetch} variant="outline" className="mt-4">
                Réessayer
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/dashboard" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au tableau de bord
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-primary" />
              Statistiques
            </h1>
            <p className="text-muted-foreground mt-1">
              Analyse détaillée de votre activité
            </p>
          </div>
        </div>

        {/* Period Selector */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Quick presets */}
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  { key: 'today', label: "Aujourd'hui" },
                  { key: '7days', label: '7 jours' },
                  { key: '30days', label: '30 jours' },
                  { key: 'thisMonth', label: 'Ce mois' },
                  { key: 'lastMonth', label: 'Mois dernier' },
                  { key: 'thisYear', label: 'Cette année' },
                ].map(({ key, label }) => (
                  <Button
                    key={key}
                    variant={periodPreset === key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePresetClick(key as PeriodPreset)}
                  >
                    {label}
                  </Button>
                ))}
              </div>

              {/* Custom date range */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="text-sm text-muted-foreground">Du</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[140px] justify-start text-left font-normal",
                        periodPreset === 'custom' && "border-primary"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(startDate, 'dd/MM/yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customStartDate}
                      onSelect={(date) => {
                        if (date) {
                          setCustomStartDate(date);
                          setPeriodPreset('custom');
                        }
                      }}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>

                <span className="text-sm text-muted-foreground">au</span>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[140px] justify-start text-left font-normal",
                        periodPreset === 'custom' && "border-primary"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(endDate, 'dd/MM/yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customEndDate}
                      onSelect={(date) => {
                        if (date) {
                          setCustomEndDate(date);
                          setPeriodPreset('custom');
                        }
                      }}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards with Trends */}
        {data?.period && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <CalendarCheck className="w-4 h-4" />
                  Total réservations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {data.period.total}
                </div>
                {data.comparison && (
                  <div className={cn(
                    "text-sm flex items-center gap-1 mt-1",
                    data.comparison.reservationsDiff >= 0 ? "text-green-600" : "text-destructive"
                  )}>
                    {data.comparison.reservationsDiff >= 0 ? (
                      <ArrowUpRight className="w-4 h-4" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4" />
                    )}
                    {formatTrend(data.comparison.reservationsDiffPercent, '%')} vs période préc.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Total personnes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">
                  {data.period.totalPeople}
                </div>
                {data.comparison && (
                  <div className={cn(
                    "text-sm flex items-center gap-1 mt-1",
                    data.comparison.peopleDiff >= 0 ? "text-green-600" : "text-destructive"
                  )}>
                    {data.comparison.peopleDiff >= 0 ? (
                      <ArrowUpRight className="w-4 h-4" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4" />
                    )}
                    {formatTrend(data.comparison.peopleDiffPercent, '%')} vs période préc.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Taux de venue
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={cn(
                  "text-3xl font-bold",
                  data.period.arrivalRate >= 75 ? 'text-green-600' : 
                  data.period.arrivalRate >= 50 ? 'text-amber-600' : 'text-destructive'
                )}>
                  {data.period.arrivalRate}%
                </div>
                {data.comparison && (
                  <div className={cn(
                    "text-sm flex items-center gap-1 mt-1",
                    data.comparison.arrivalRateDiff >= 0 ? "text-green-600" : "text-destructive"
                  )}>
                    {data.comparison.arrivalRateDiff >= 0 ? (
                      <ArrowUpRight className="w-4 h-4" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4" />
                    )}
                    {formatTrend(data.comparison.arrivalRateDiff, ' pts')}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Annulations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-destructive">
                  {data.period.cancelled}
                </div>
                {(data.period.noShow ?? 0) > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    dont {data.period.noShow} no-show{(data.period.noShow ?? 0) > 1 ? 's' : ''}
                  </p>
                )}
                {data.comparison && (
                  <div className={cn(
                    "text-sm flex items-center gap-1 mt-1",
                    data.comparison.cancelledDiff <= 0 ? "text-green-600" : "text-destructive"
                  )}>
                    {data.comparison.cancelledDiff <= 0 ? (
                      <ArrowDownRight className="w-4 h-4" />
                    ) : (
                      <ArrowUpRight className="w-4 h-4" />
                    )}
                    {formatTrend(data.comparison.cancelledDiff)} vs période préc.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Children KPI - only if there are children */}
            {data.period.totalChildren > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2">
                    <Baby className="w-4 h-4" />
                    Dont enfants
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-muted-foreground">
                    {data.period.totalChildren}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    sur {data.period.totalPeople} personnes au total
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tabs for different views */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="weather" className="flex items-center gap-1.5">
              <CloudSun className="w-4 h-4" />
              Météo
            </TabsTrigger>
            <TabsTrigger value="trends">Tendances</TabsTrigger>
            <TabsTrigger value="details">Détails</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Status Distribution Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Répartition des statuts</CardTitle>
                  <CardDescription>
                    Distribution des réservations par statut
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center">
                    <div className="h-[200px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={data?.statusDistribution || []}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {(data?.statusDistribution || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Légende personnalisée avec chiffres et pourcentages */}
                    <div className="flex flex-wrap justify-center gap-4 mt-4">
                      {(data?.statusDistribution || []).map((entry, index) => {
                        const total = data?.period?.total || 1;
                        const percent = ((entry.value / total) * 100).toFixed(1);
                        return (
                          <div key={entry.name} className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full shrink-0" 
                              style={{ backgroundColor: COLORS[index % COLORS.length] }} 
                            />
                            <span className="text-sm">
                              {entry.name}: <strong>{entry.value}</strong> ({percent}%)
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Day of Week Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Jours les plus actifs</CardTitle>
                  <CardDescription>
                    Réservations par jour de la semaine
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={data?.byDayOfWeek || []}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                        <YAxis 
                          dataKey="day" 
                          type="category" 
                          width={80}
                          tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value: number, name: string) => [value, name === 'count' ? 'Réservations' : 'Personnes']}
                        />
                        <Bar dataKey="count" fill="hsl(var(--primary))" name="Réservations" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Source Distribution */}
            {data?.bySource && data.bySource.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Répartition par source</CardTitle>
                  <CardDescription>
                    Provenance des réservations sur la période
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Horizontal Bar Chart */}
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={data.bySource}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <YAxis 
                            dataKey="source" 
                            type="category" 
                            width={120}
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                            formatter={(value: number, name: string) => [value, name === 'count' ? 'Réservations' : 'Personnes']}
                          />
                          <Bar dataKey="count" fill="hsl(var(--primary))" name="Réservations" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Summary Table */}
                    <div className="overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Source</TableHead>
                            <TableHead className="text-right">Résa.</TableHead>
                            <TableHead className="text-right">Pers.</TableHead>
                            <TableHead className="text-right">Taux venue</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.bySource.map((item) => (
                            <TableRow key={item.source}>
                              <TableCell className="font-medium">{item.source}</TableCell>
                              <TableCell className="text-right">{item.count}</TableCell>
                              <TableCell className="text-right">{item.people}</TableCell>
                              <TableCell className="text-right">
                                <span className={cn(
                                  "font-medium",
                                  item.arrivalRate >= 75 ? 'text-green-600' :
                                  item.arrivalRate >= 50 ? 'text-amber-600' : 'text-destructive'
                                )}>
                                  {item.arrivalRate}%
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Weather Tab */}
          <TabsContent value="weather" className="space-y-4">
            {weatherLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : !weatherData?.hasData ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Aucune donnée de réservation disponible pour cette période.
                </AlertDescription>
              </Alert>
            ) : !weatherData?.hasWeatherData ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {weatherData?.weatherDataNote || "Les données météo ne sont pas disponibles pour cette période. Essayez de sélectionner une période plus ancienne (au moins 5 jours avant aujourd'hui)."}
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {/* Insights Banner */}
                {weatherData.insights && (
                  <Alert className="bg-gradient-to-r from-amber-50 to-blue-50 border-amber-200 dark:from-amber-950/30 dark:to-blue-950/30 dark:border-amber-800">
                    <CloudSun className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-sm">
                      <strong>Insight :</strong> Les jours <span className="font-semibold">{weatherData.insights.bestWeatherType.toLowerCase()}</span> génèrent 
                      en moyenne <span className="font-semibold text-primary">{weatherData.insights.bestWeatherAvg}</span> réservations/jour
                      {weatherData.insights.rainImpact > 0 && (
                        <>, soit <span className="text-green-600 font-semibold">+{weatherData.insights.rainImpact}%</span> de plus que les jours pluvieux</>
                      )}.
                      {weatherData.insights.optimalTemperature && (
                        <> La température optimale est <span className="font-semibold">{weatherData.insights.optimalTemperature}</span>.</>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Weather Type Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {weatherData.byWeatherType.map((weather) => (
                    <Card key={weather.key} className="relative overflow-hidden">
                      <div 
                        className="absolute inset-0 opacity-10" 
                        style={{ backgroundColor: WEATHER_COLORS[weather.key] || 'transparent' }}
                      />
                      <CardHeader className="pb-2">
                        <CardDescription className="flex items-center gap-2">
                          <WeatherIcon category={weather.key} className="w-5 h-5" />
                          {weather.category}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-1">
                        <div className="text-2xl font-bold">{weather.totalReservations}</div>
                        <div className="text-xs text-muted-foreground">
                          Moy: {weather.avgReservations}/jour • {weather.daysCount} jours
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className={cn(
                            "font-medium",
                            weather.arrivalRate >= 75 ? "text-green-600" : 
                            weather.arrivalRate >= 50 ? "text-amber-600" : "text-destructive"
                          )}>
                            Venue: {weather.arrivalRate}%
                          </span>
                          <span className="text-destructive">
                            Annul: {weather.cancellationRate}%
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Daily Weather Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Thermometer className="w-5 h-5 text-primary" />
                      Réservations et température
                    </CardTitle>
                    <CardDescription>
                      Évolution quotidienne des réservations avec les conditions météo
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={weatherData.dailyData.slice(-30)}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                            tickFormatter={(value) => format(new Date(value), 'dd/MM', { locale: fr })}
                          />
                          <YAxis 
                            yAxisId="left"
                            tick={{ fill: 'hsl(var(--muted-foreground))' }}
                          />
                          <YAxis 
                            yAxisId="right"
                            orientation="right"
                            tick={{ fill: 'hsl(var(--muted-foreground))' }}
                            tickFormatter={(value) => `${value}°C`}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                            labelFormatter={(value) => format(new Date(value), 'EEEE d MMMM', { locale: fr })}
                            formatter={(value: number, name: string) => {
                              if (name === 'tempMax') return [`${value}°C`, 'Temp. max'];
                              if (name === 'total') return [value, 'Réservations'];
                              if (name === 'arrived') return [value, 'Venues'];
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Area 
                            yAxisId="left"
                            type="monotone" 
                            dataKey="total" 
                            fill="hsl(var(--primary) / 0.2)" 
                            stroke="hsl(var(--primary))"
                            name="Réservations"
                          />
                          <Bar 
                            yAxisId="left"
                            dataKey="arrived" 
                            fill="hsl(var(--primary))" 
                            name="Venues"
                            radius={[2, 2, 0, 0]}
                          />
                          <Line 
                            yAxisId="right"
                            type="monotone" 
                            dataKey="tempMax" 
                            stroke="hsl(var(--destructive))"
                            strokeWidth={2}
                            dot={false}
                            name="Temp. max"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Temperature Range Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>Impact de la température</CardTitle>
                    <CardDescription>
                      Moyenne des réservations par plage de température
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={weatherData.byTemperature} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <YAxis 
                            dataKey="range" 
                            type="category" 
                            width={70}
                            tick={{ fill: 'hsl(var(--muted-foreground))' }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                            formatter={(value: number, name: string) => {
                              if (name === 'avgReservations') return [value, 'Moy. réservations/jour'];
                              if (name === 'arrivalRate') return [`${value}%`, 'Taux de venue'];
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Bar 
                            dataKey="avgReservations" 
                            fill="hsl(var(--primary))" 
                            name="Moy. réservations/jour"
                            radius={[0, 4, 4, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="trends" className="space-y-4">
            {/* 12 Months Evolution */}
            <Card>
              <CardHeader>
                <CardTitle>Évolution sur 12 mois</CardTitle>
                <CardDescription>
                  Réservations venues vs annulées par mois
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="name" 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <YAxis 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="venues" fill="hsl(var(--primary))" name="Venues" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="annulées" fill="hsl(var(--destructive))" name="Annulées" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Arrival Rate Evolution Line Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Taux de venue mensuel</CardTitle>
                <CardDescription>
                  Évolution du taux de venue sur les 12 derniers mois
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="name" 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <YAxis 
                        className="text-xs"
                        domain={[0, 100]}
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value: number) => [`${value}%`, 'Taux de venue']}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="tauxVenue" 
                        stroke="hsl(var(--chart-3))" 
                        name="Taux de venue" 
                        strokeWidth={2}
                        dot={{ fill: 'hsl(var(--chart-3))' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Details Tab */}
          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle>Historique mensuel détaillé</CardTitle>
                <CardDescription>
                  Récapitulatif complet de toutes les réservations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mois</TableHead>
                      <TableHead className="text-center">Total</TableHead>
                      <TableHead className="text-center">Venues</TableHead>
                      <TableHead className="text-center">Personnes</TableHead>
                      <TableHead className="text-center">Annulées</TableHead>
                      <TableHead className="text-center">Planifiées</TableHead>
                      <TableHead className="text-center">Taux venue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!data?.monthlyData?.length ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          Aucune donnée disponible
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.monthlyData.map((month) => (
                        <TableRow key={month.month}>
                          <TableCell className="font-medium">{month.label}</TableCell>
                          <TableCell className="text-center font-semibold">
                            {month.total}
                          </TableCell>
                          <TableCell className="text-center font-semibold text-primary">
                            {month.arrived}
                          </TableCell>
                          <TableCell className="text-center">
                            {month.arrivedPeople}
                          </TableCell>
                          <TableCell className="text-center text-destructive">
                            {month.cancelled}
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">
                            {month.planned}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={month.arrivalRate >= 75 ? "text-green-600" : month.arrivalRate >= 50 ? "text-amber-600" : "text-destructive"}>
                              {month.arrivalRate}%
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Statistics;
